import React from 'react';
import { motion } from 'framer-motion';
import { Music2, Languages, Mic2, Star } from 'lucide-react';

const AnthemSection = () => {
  return (
    <section className="bg-[#fcfaf7] py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        
        {/* Header Section */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-full border border-amber-100 text-amber-700 mb-6"
          >
            <Star className="w-3 h-3 fill-amber-500" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em]">Diocesan Song</span>
            <Star className="w-3 h-3 fill-amber-500" />
          </motion.div>
          
          <h1 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 mb-6 flex justify-center items-center gap-4">
            <Music2 className="text-amber-500 w-10 h-10 md:w-12 md:h-12" />
            Diocesan Anthem
          </h1>
          <p className="text-slate-500 font-serif italic text-lg max-w-2xl mx-auto">
            "A melody of redemption, a shout of Hallelujah, and a celebration of the Living God."
          </p>
        </div>

        {/* Versions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start relative">
          {/* Vertical Divider for Desktop */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-slate-200" />

          {/* English Section */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-10"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8 bg-slate-300" />
              <Languages className="w-4 h-4 text-slate-400" />
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">English Version</h2>
            </div>

            <div className="space-y-12 font-serif text-lg md:text-xl text-slate-700 leading-relaxed italic">
              <div>
                <span className="text-xs font-bold text-amber-600 mb-2 block tracking-tighter">STANZA 1</span>
                <p>
                  Let us worship the Lord, the redeemed of the Lord; <br />
                  Let us praise the Lord, the purchased by the blood; <br />
                  Let us sing to God, who joy belongs, <br />
                  <span className="text-slate-900 font-bold not-italic">Ibadan South Diocese</span> shout Halle-luyah.
                </p>
              </div>

              <div>
                <span className="text-xs font-bold text-amber-600 mb-2 block tracking-tighter">STANZA 2</span>
                <p>
                  The spring of joy, the anointed of the Lord, <br />
                  The fountain of life, the believer of His word; <br />
                  The city set on the hill, cannot be hid; <br />
                  <span className="text-slate-900 font-bold not-italic">Ibadan South Diocese</span> shout Halle-luyah.
                </p>
              </div>

              {/* English Chorus */}
              <div className="bg-white p-8 rounded-[2rem] border border-amber-100 shadow-xl shadow-amber-900/5 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-5">
                  <Mic2 className="w-20 h-20" />
                </div>
                <span className="text-xs font-bold text-amber-600 mb-4 block tracking-widest uppercase">Refrain (Ègbé)</span>
                <p className="relative z-10 text-slate-900 font-medium">
                  The joy of the Lord we bring, praise to JESUS we ring; <br />
                  For the joy of the Lord is our strength, Shout for joy, shout for joy <br />
                  For the Living God is the One we serve, Shout for joy, shout for joy <br />
                  <span className="font-bold underline decoration-amber-500 underline-offset-4">Ibadan South Diocese</span> shout Halle-luyah.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Yoruba Section */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-10"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8 bg-slate-300" />
              <Languages className="w-4 h-4 text-slate-400" />
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">Yoruba Version</h2>
            </div>

            <div className="space-y-12 font-serif text-lg md:text-xl text-slate-700 leading-relaxed italic">
              <div>
                <span className="text-xs font-bold text-amber-600 mb-2 block tracking-tighter">ẸSẸ KÌÍNÍ</span>
                <p>
                  Jẹ́ ká sin Olúwa, ẹ̀yin tá a rà padà; <br />
                  Jẹ́ ká yìn Olúwa, ẹ̀yin tá fẹ́jẹ̀ rà; <br />
                  K'orin s'Olorun, Ìjọ aláyọ; <br />
                  <span className="text-slate-900 font-bold not-italic">Ibadan South Diocese</span> ké Halẹlúyà.
                </p>
              </div>

              <div>
                <span className="text-xs font-bold text-amber-600 mb-2 block tracking-tighter">ẸSẸ KEJÌ</span>
                <p>
                  Orísun ayọ̀, ẹni oróró Olúwa; <br />
                  Orísun ìyẹ, ẹni tó gbà ọ̀rọ̀ Rẹ̀ gbọ́; <br />
                  Ìlú tá tẹ̀ dó sórí òkè, kò le fárasí; <br />
                  <span className="text-slate-900 font-bold not-italic">Ibadan South Diocese</span> ké Halẹlúyà.
                </p>
              </div>

              {/* Yoruba Chorus */}
              <div className="bg-slate-900 p-8 rounded-[2rem] text-white shadow-2xl relative overflow-hidden">
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-amber-500/10 rounded-full" />
                <span className="text-xs font-bold text-amber-500 mb-4 block tracking-widest uppercase italic">Ègbé (Chorus)</span>
                <p className="relative z-10 font-medium">
                  Ayọ̀ Ọlọ́run là mú wá, Ìyìn sí JESU là ńkede <br />
                  Ayọ̀ Ọlọ́run ni agbára wa, Hó f'ayọ̀, Hó f'ayọ̀ <br />
                  Ọlọ́run Alààyè ni àwa ńsin, Hó f'ayọ̀, Hó f'ayọ̀ <br />
                  <span className="text-amber-500 font-bold">Ibadan South Diocese</span> ké Halẹlúyà.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Hallelujah Footer */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mt-24 text-center border-t border-slate-200 pt-16"
        >
          <div className="inline-block relative">
            <h3 className="text-amber-600 font-serif text-3xl md:text-4xl font-bold tracking-widest uppercase animate-pulse">
              Halleluiah!
            </h3>
            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.5em] mt-4">
              Seven Times Praise to the King
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-8 opacity-40 grayscale hover:grayscale-0 transition-all">
            {[...Array(7)].map((_, i) => (
              <span key={i} className="text-slate-400 font-serif italic text-sm">Hallelujah</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AnthemSection;